import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { PageHeader, Section, EmptyState } from "@/components/Shared";
import api from "@/lib/api";
import { TL, CARI_TYPE_LABEL } from "@/lib/format";
import { ArrowDownRight, ArrowUpRight, Banknote, Wallet, TrendingUp, TrendingDown, Users, Lock } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Legend } from "recharts";
import { Link } from "react-router-dom";

function Metric({ label, value, icon: Icon, tone = "default", testid }) {
  const toneClass = {
    default: "text-foreground",
    success: "text-[hsl(var(--success))]",
    destructive: "text-destructive",
    primary: "text-primary",
  }[tone];
  return (
    <div className="border border-border rounded-md bg-background p-5 hover:border-foreground/20 transition-colors" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-wider text-foreground/50">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-foreground/40" strokeWidth={1.5} />}
      </div>
      <div className={`font-heading text-3xl font-medium tracking-tight mt-3 tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [top, setTop] = useState({ receivables: [], payables: [] });

  useEffect(() => {
    (async () => {
      const [s, m, t] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/dashboard/monthly-flow?months=6"),
        api.get("/dashboard/top-cariler"),
      ]);
      setSummary(s.data);
      setMonthly(m.data);
      setTop(t.data);
    })().catch(console.error);
  }, []);

  return (
    <Layout>
      <PageHeader
        title="Kontrol Paneli"
        subtitle="Nakit akışı, cari alacak/verecek ve hesap bakiye özeti"
        testId="dashboard-header"
      />

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="metrics-grid">
          <Metric label="Toplam Bakiye" value={TL(summary.total_balance)} icon={Wallet} tone="primary" testid="metric-total-balance" />
          <Metric label="Toplam Alacak" value={TL(summary.total_receivable)} icon={ArrowUpRight} tone="success" testid="metric-total-receivable" />
          <Metric label="Toplam Borç" value={TL(summary.total_payable)} icon={ArrowDownRight} tone="destructive" testid="metric-total-payable" />
          <Metric label="Net Nakit Akışı" value={TL(summary.net_cash_flow)} icon={TrendingUp} tone={summary.net_cash_flow >= 0 ? "success" : "destructive"} testid="metric-net-cash-flow" />
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <Metric label="Kasa Bakiye" value={TL(summary.kasa_total)} icon={Banknote} testid="metric-kasa" />
          <Metric label="Banka Bakiye" value={TL(summary.banka_total)} icon={Wallet} testid="metric-banka" />
          <Metric label="Blokede Bakiye" value={TL(summary.blocked_balance || 0)} icon={Lock} tone={summary.blocked_balance > 0 ? "destructive" : "default"} testid="metric-blocked" />
          <Metric label="Toplam Gelir" value={TL(summary.total_income)} icon={TrendingUp} tone="success" testid="metric-income" />
        </div>
      )}

      <Section title="Aylık Nakit Akışı (Son 6 Ay)">
        <div className="border border-border rounded-md p-6 bg-background">
          {monthly.length === 0 || monthly.every((m) => m.gelir === 0 && m.gider === 0) ? (
            <EmptyState title="Henüz hareket yok" hint="Kasa/banka hareketleri ekleyerek grafiği oluşturun." testId="empty-monthly" />
          ) : (
            <div className="h-72" data-testid="monthly-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 6 }}
                    formatter={(v) => TL(v)}
                  />
                  <Legend />
                  <Bar dataKey="gelir" fill="hsl(var(--chart-1))" name="Gelir" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="gider" fill="hsl(var(--chart-2))" name="Gider" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="En Yüksek Alacaklar">
          <div className="border border-border rounded-md bg-background overflow-hidden" data-testid="top-receivables">
            {top.receivables.length === 0 ? (
              <EmptyState title="Alacak bulunmuyor" testId="empty-receivables" />
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-foreground/70">
                  <tr>
                    <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Cari</th>
                    <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Tür</th>
                    <th className="text-right py-3 px-4 text-[11px] uppercase tracking-wider">Bakiye</th>
                  </tr>
                </thead>
                <tbody>
                  {top.receivables.map((c) => (
                    <tr key={c.id} className="border-t border-border">
                      <td className="py-3 px-4"><Link className="text-primary hover:underline" to={`/cariler/${c.id}`}>{c.name}</Link></td>
                      <td className="py-3 px-4 text-foreground/70">{CARI_TYPE_LABEL[c.type]}</td>
                      <td className="py-3 px-4 text-right tabular-nums text-[hsl(var(--success))] font-medium">{TL(c.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Section>

        <Section title="En Yüksek Borçlar">
          <div className="border border-border rounded-md bg-background overflow-hidden" data-testid="top-payables">
            {top.payables.length === 0 ? (
              <EmptyState title="Borç bulunmuyor" testId="empty-payables" />
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-foreground/70">
                  <tr>
                    <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Cari</th>
                    <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Tür</th>
                    <th className="text-right py-3 px-4 text-[11px] uppercase tracking-wider">Bakiye</th>
                  </tr>
                </thead>
                <tbody>
                  {top.payables.map((c) => (
                    <tr key={c.id} className="border-t border-border">
                      <td className="py-3 px-4"><Link className="text-primary hover:underline" to={`/cariler/${c.id}`}>{c.name}</Link></td>
                      <td className="py-3 px-4 text-foreground/70">{CARI_TYPE_LABEL[c.type]}</td>
                      <td className="py-3 px-4 text-right tabular-nums text-destructive font-medium">{TL(c.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Section>
      </div>
    </Layout>
  );
}
